import { CommonMessage } from '@frontend/lang';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot, SlotRendering, SlotType } from '@frontend/slot/types';
import React, { useCallback, useEffect, useState } from 'react';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../components/slot-layout-cell';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { openSlot } from '../../utils/slot-opener';

export interface ViewContentViewProps {
    accountId: string | undefined;
    spotId: string | undefined;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    canGoPrevious: boolean;
    canGoNext: boolean;
    onPreviousModule: () => void;
    onNextModule: () => void;
    setCurrentModule: (module: Module | null) => void;
    setSelectedSlot: (slot: Slot | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
    isLoading: boolean;
    error: React.ReactNode | null;
    openAllSlotsInModule: (module: Module) => Promise<void>;
    isOpeningModuleSlots: boolean;
}

export const useViewContent = (): ViewContentViewProps => {
    const navigationState = useAppSelector((state) => state.navigation);
    const spotId = navigationState.spot?.id;
    const accountId = navigationState.iot?.account_id;
    const iotId = navigationState.iot?.id;

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<React.ReactNode | null>(null);
    const [isOpeningModuleSlots, setIsOpeningModuleSlots] = useState(false);

    useEffect(() => {
        if (!spotId) {
            setModules(null);
            setAllSlots(null);
            setSelectedSlot(null);
            setIsLoading(false);
            setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            return;
        }

        setError(null);
        setIsLoading(true);
        Promise.all([ModuleClient.fetchModules({ spot_id: spotId }), SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' })])
            .then(([modulesRes, slotsRes]) => {
                const sorted = modulesRes.results.sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sorted);
                setAllSlots(slotsRes.results);
            })
            .catch(() => setError(CommonMessage.ERRORS.FAILED_TO_LOAD))
            .finally(() => setIsLoading(false));
    }, [spotId]);

    const navigate = useKioskNavigate();
    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;
    const canGoPrevious = currentModuleIndex > 0;
    const canGoNext = modules ? currentModuleIndex < modules.length - 1 : false;

    const onPreviousModule = () => {
        if (canGoPrevious) setCurrentModuleIndex(currentModuleIndex - 1);
    };

    const onNextModule = () => {
        if (canGoNext) setCurrentModuleIndex(currentModuleIndex + 1);
    };

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) setCurrentModuleIndex(index);
        }
        setSelectModuleModalOpen(false);
    };

    const openAllSlotsInModule = useCallback(
        async (module: Module) => {
            if (!accountId || !spotId || !iotId || !allSlots) return;
            const slotsInModule = allSlots.filter((s) => s.module_id === module.id && s.type !== SlotType.TERMINAL);
            if (slotsInModule.length === 0) return;
            setIsOpeningModuleSlots(true);
            for (const slot of slotsInModule) {
                await openSlot({
                    accountId,
                    spotId,
                    moduleId: slot.module_id,
                    slotId: slot.id,
                    iotId
                });
            }
            setIsOpeningModuleSlots(false);
        },
        [accountId, spotId, iotId, allSlots]
    );

    const handleSlotClick = useCallback(
        (slot: Slot) => {
            if (selectedSlot?.id === slot.id) {
                navigate(`/spots/layout/slot/${slot.id}`);
            } else {
                setSelectedSlot(slot);
            }
        },
        [selectedSlot?.id, navigate]
    );

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slotId = slotRendering.slot_ids.find((id) => allSlots?.some((s) => s.id === id));
        const slot = allSlots?.find((s) => s.id === slotId) ?? null;
        const isSelected = selectedSlot != null && slotRendering.slot_ids.includes(selectedSlot.id);
        const slotNumber = slotLayoutDisplayNumber(slot);
        const hasContent = slotRendering.occupied === true || (slotRendering.stocks?.length ?? 0) > 0 || (slotRendering.transactions?.length ?? 0) > 0;
        const isSelectable = slot && slot.type !== SlotType.TERMINAL;

        return (
            <SlotLayoutCell
                slotNumber={slotNumber}
                isSelected={isSelected}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={hasContent}
                onSelect={isSelectable && slot ? () => handleSlotClick(slot) : undefined}
            />
        );
    };

    return {
        accountId,
        spotId,
        currentModule,
        modules,
        selectedSlot,
        canGoPrevious,
        canGoNext,
        onPreviousModule,
        onNextModule,
        setCurrentModule,
        setSelectedSlot,
        slotComponent,
        selectModuleModalOpen,
        setSelectModuleModalOpen,
        isLoading,
        error,
        openAllSlotsInModule,
        isOpeningModuleSlots
    };
};
